// src/pages/admin/AdminCompaniesPage.tsx
import React, { useEffect, useState } from 'react';
import {
    AdminCompany,
    listCompanies,
    createCompany,
    updateCompany,
    deleteCompany,
    createCompanyAdmin,
} from '../../features/admin/api';
import { useToast } from 'src/shared/ui/toast/useToast';

const AdminCompaniesPage: React.FC = () => {
    const toast = useToast();

    const [rows, setRows] = useState<AdminCompany[]>([]);
    const [loading, setLoading] = useState(false);
    const [newName, setNewName] = useState('');

    const fetchCompanies = async () => {
        setLoading(true);
        try {
            const list = await listCompanies();
            setRows(list);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const handleCreate = async () => {
        if (!newName.trim()) {
            toast.warning('Company 이름을 입력해 주세요.', {
                title: '입력 필요',
            });
            return;
        }
        setLoading(true);
        try {
            const created = await createCompany({ name: newName.trim() });
            setRows((prev) => [created, ...prev]);
            setNewName('');
            toast.success(`Company "${created.name}"를 추가했습니다.`, {
                title: '추가 완료',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (c: AdminCompany) => {
        const nextStatus = c.status === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE';
        setLoading(true);
        try {
            const updated = await updateCompany(c.id, { status: nextStatus });
            setRows((prev) => prev.map((x) => (x.id === updated.id ? updated : x)));
            toast.success(`"${updated.name}" 회사를 ${updated.status === 'ACTIVE' ? '활성화' : '차단'}했습니다.`, {
                title: '상태 변경 완료',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (c: AdminCompany) => {
        if (!window.confirm(`정말로 "${c.name}" 회사를 삭제하시겠습니까?`)) return;
        setLoading(true);
        try {
            await deleteCompany(c.id);
            setRows((prev) => prev.filter((x) => x.id !== c.id));
            toast.success(`Company "${c.name}"를 삭제했습니다.`, {
                title: '삭제 완료',
            });
        } catch (err: any) {
            toast.error('삭제할 수 없습니다. (프로젝트 등과 연결되어 있을 수 있습니다)', {
                title: '삭제 실패',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAssignAdmin = async (c: AdminCompany) => {
        const email = window.prompt(`"${c.name}" 회사의 Company Admin 이메일을 입력하세요.`);
        if (!email) return;

        const name = window.prompt(`표시 이름(선택, 비워두면 이메일과 같은 이름으로 저장됩니다):`) || undefined;

        setLoading(true);
        try {
            const res = await createCompanyAdmin(c.id, { email, name });
            const plainPassword = res.plainPassword as string | undefined;

            toast.success(
                [
                    'Company Admin이 설정되었습니다.',
                    `이메일: ${res.user.email}`,
                    name ? `이름: ${name}` : `이름: ${email}`,
                    plainPassword ? `초기 비밀번호: ${plainPassword}` : '',
                    plainPassword ? '첫 로그인 후 비밀번호를 변경해야 합니다.' : '',
                ]
                    .filter(Boolean)
                    .join('\n'),
                {
                    title: '설정 완료',
                    duration: 5000,
                },
            );
        } catch (err: any) {
            toast.error(err?.response?.data?.error?.message ?? 'Company Admin 설정 실패', {
                title: '설정 실패',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-gray-900">Companies</h1>
                {loading && <span className="text-xs text-gray-500">Loading...</span>}
            </div>

            <div className="flex items-center gap-2">
                <input
                    className="border rounded px-2 py-1 text-sm flex-1"
                    placeholder="새 Company 이름"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                />
                <button
                    className="px-3 py-1 rounded text-sm bg-sky-600 text-white hover:bg-sky-700"
                    onClick={handleCreate}
                >
                    추가
                </button>
            </div>

            <div className="border rounded-lg overflow-hidden bg-white shadow-sm">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide">
                        <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Status</th>
                            <th className="px-3 py-2 text-left">Company Admin</th>
                            <th className="px-3 py-2 text-left">Projects</th>
                            <th className="px-3 py-2 text-left">Created At</th>
                            <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-3 py-6 text-center text-gray-400">
                                    아직 등록된 Company가 없습니다.
                                </td>
                            </tr>
                        )}
                        {rows.map((c) => (
                            <tr key={c.id} className="border-t">
                                <td className="px-3 py-2">{c.name}</td>
                                <td className="px-3 py-2">
                                    <span
                                        className={
                                            'inline-flex rounded-full px-2 py-0.5 text-[11px] ' +
                                            (c.status === 'ACTIVE'
                                                ? 'bg-emerald-50 text-emerald-700'
                                                : 'bg-rose-50 text-rose-700')
                                        }
                                    >
                                        {c.status}
                                    </span>
                                </td>
                                {/* ✅ Company Admin 표시 */}
                                <td className="px-3 py-2 text-xs text-gray-700">
                                    {(c.members ?? []).length === 0 ? (
                                        <span className="text-gray-400">-</span>
                                    ) : (
                                        <div className="space-y-1">
                                            {c.members!.map((m) => (
                                                <div key={m.user.id} className="leading-tight">
                                                    <div className="font-medium">{m.user.name}</div>
                                                    <div className="text-gray-500">{m.user.email}</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </td>

                                {/* ✅ Projects 표시 */}
                                <td className="px-3 py-2 text-xs text-gray-700">
                                    {(c.projects ?? []).length === 0 ? (
                                        <span className="text-gray-400">-</span>
                                    ) : (
                                        <div className="space-y-1">
                                            {c.projects!.slice(0, 3).map((p) => (
                                                <div key={p.id} className="leading-tight">
                                                    {p.name}{' '}
                                                    <span className="text-gray-400">
                                                        ({p.status}
                                                        {p.code ? ` / ${p.code}` : ''})
                                                    </span>
                                                </div>
                                            ))}
                                            {c.projects!.length > 3 && (
                                                <div className="text-gray-400">+{c.projects!.length - 3} more</div>
                                            )}
                                        </div>
                                    )}
                                </td>
                                <td className="px-3 py-2 text-xs text-gray-500">
                                    {new Date(c.createdAt).toLocaleDateString()}
                                </td>
                                <td className="px-3 py-2 text-right space-x-2">
                                    <button
                                        className="text-xs text-emerald-700 hover:underline"
                                        onClick={() => handleAssignAdmin(c)}
                                    >
                                        관리자 지정
                                    </button>
                                    <button
                                        className="text-xs text-sky-700 hover:underline"
                                        onClick={() => handleToggleStatus(c)}
                                    >
                                        {c.status === 'ACTIVE' ? '차단' : '차단 해제'}
                                    </button>
                                    <button
                                        className="text-xs text-rose-600 hover:underline"
                                        onClick={() => handleDelete(c)}
                                    >
                                        삭제
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminCompaniesPage;
